// src/App.js
import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import ErrorBoundary from "./components/ErrorBoundary.jsx";

// Eager: caminho crítico (entrada anónima e autenticação). Tudo o resto é
// carregado sob procura para não pesar no primeiro acesso ao site público.
import Login from "./pages/Login.jsx";
import ForgotPassword from "./pages/ForgotPassword.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import PublicHome from "./pages/PublicHome.jsx";
import NotFoundPage from "./pages/NotFoundPage.jsx";
import ProtectedRoute from "./auth/ProtectedRoute.jsx";
import { useAuth } from "./auth/AuthProvider.jsx";
import useSidebarCollapsed from "./hooks/useSidebarCollapsed";

import "./App.css";

// Lazy: páginas do painel (recharts, xlsx, jspdf, react-quill, sweetalert2…)
// só descarregam quem entra no painel.
const WhatsAppSharePanel = lazy(() => import("./pages/WhatsAppSharePanel"));
const OfferingsImport = lazy(() => import("./pages/OfferingsImport"));
const Services = lazy(() => import("./pages/Services"));
const Schedules = lazy(() => import("./pages/Schedules"));
const Reports = lazy(() => import("./pages/Reports"));
const ServiceReport = lazy(() => import("./pages/ServiceReport.jsx"));
const MembersPage = lazy(() => import("./pages/MembersPage.jsx"));
const DepartmentsPage = lazy(() => import("./pages/DepartmentsPage.jsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.jsx"));
const AnnualPlan = lazy(() => import("./pages/AnnualPlan.jsx"));
const UsersPage = lazy(() => import("./pages/UsersPage.jsx"));
const RolesPage = lazy(() => import("./pages/RolesPage.jsx"));
const ActivityPage = lazy(() => import("./pages/ActivityPage.jsx"));
const PublicAdmin = lazy(() => import("./pages/PublicAdmin.jsx"));
const PlatformPage = lazy(() => import("./pages/PlatformPage.jsx"));
const ChurchProfilePage = lazy(() => import("./pages/ChurchProfilePage.jsx"));
const SecurityPage = lazy(() => import("./pages/SecurityPage.jsx"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600 dark:text-slate-300 text-sm">
      A carregar…
    </div>
  );
}

// Raiz: o visitante anónimo vê o site público diretamente em "/"; o utilizador
// autenticado segue para o painel. "/public" continua a funcionar como atalho.
function Home() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-600 text-sm">
        A carregar…
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return <PublicHome />;
}

function Shell() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navbarHiddenPaths = ["/login", "/recuperar-palavra-passe", "/redefinir-palavra-passe"];
  const showsPublicHome =
    pathname.startsWith("/public") || pathname.startsWith("/igreja/") || (pathname === "/" && !user);
  const hideNavbar = navbarHiddenPaths.includes(pathname) || showsPublicHome;

  // Instalar a partir do site dá a app do site; a partir do login ou do painel, a app do painel.
  useEffect(() => {
    const link = document.getElementById("app-manifest");
    const href = showsPublicHome ? "/manifest.json" : "/manifest-painel.json";
    if (link && link.getAttribute("href") !== href) link.setAttribute("href", href);
  }, [showsPublicHome]);

  const [sidebarCollapsed] = useSidebarCollapsed();

  // Com navegação, o conteúdo começa depois da barra lateral (recolhida: só ícones).
  return (
    <div
      className={
        hideNavbar ? undefined : `transition-[padding] duration-200 ${sidebarCollapsed ? "lg:pl-20" : "lg:pl-64"}`
      }
    >
      {!hideNavbar && <Navbar user={user} />}

      <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/recuperar-palavra-passe" element={<ForgotPassword />} />
        <Route path="/redefinir-palavra-passe" element={<ResetPassword />} />
        <Route path="/" element={<Home />} />

        <Route
          path="/whatsapp"
          element={
            <ProtectedRoute perm="services.view">
              <WhatsAppSharePanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/importar-ofertas"
          element={
            <ProtectedRoute perm="offerings.edit">
              <OfferingsImport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/services"
          element={
            <ProtectedRoute perm="services.view">
              <Services />
            </ProtectedRoute>
          }
        />
{/* ✅ nova rota */}
  <Route
    path="/dashboard"
    element={
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    }
  />
        <Route
          path="/schedules"
          element={
            <ProtectedRoute perm="services.view">
              <Schedules />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute perm="finance.view">
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relatorio-do-culto"
          element={
            <ProtectedRoute perm="reports.edit">
              <ServiceReport />
            </ProtectedRoute>
          }
        />

        {/* ✅ novas rotas */}
        <Route
          path="/membros"
          element={
            <ProtectedRoute perm="members.view">
              <MembersPage />
            </ProtectedRoute>
          }
        />

     <Route
  path="/plano-anual"
  element={
    <ProtectedRoute perm="plan.view">
      <AnnualPlan />
    </ProtectedRoute>
  }
      />
        <Route
          path="/departamentos"
          element={
            <ProtectedRoute perm="members.view">
              <DepartmentsPage />
            </ProtectedRoute>
          }
        />

        {/* Página pública: igreja original em "/" e "/public"; outras igrejas em /igreja/<slug> */}
        <Route path="/public" element={<PublicHome />} />
        <Route path="/igreja/:churchSlug" element={<PublicHome />} />

        <Route
          path="/seguranca"
          element={
            <ProtectedRoute>
              <SecurityPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dados-da-igreja"
          element={
            <ProtectedRoute perm="church.edit">
              <ChurchProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/plataforma"
          element={
            <ProtectedRoute superAdmin>
              <PlatformPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/perfis"
          element={
            <ProtectedRoute perm="users.manage">
              <RolesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/utilizadores"
          element={
            <ProtectedRoute perm="users.manage">
              <UsersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/registo-de-atividade"
          element={
            <ProtectedRoute perm="activity.view">
              <ActivityPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/public-admin"
          element={
            <ProtectedRoute perm="site.edit">
              <PublicAdmin />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </div>
  );
}

export default function App() {
  return <Shell />;
}
